/**
 * useTaskThreadSubscription.ts — WebSocket subscription hook for Spec 017.
 *
 * Connects to equanaut-master's /api/companies/{companyId}/events/ws,
 * dispatches task.* events to the Redux taskThreads slice, handles
 * close codes 4040 (full_refetch_required) and 1008 (auth expiry),
 * and persists reducer-applied lastEventId to localStorage per companyId (FR-008).
 *
 * On mount:
 *  1. Calls POST /worktrees to identify as equa_web_sidebar (FR-012, T017-29)
 *  2. Reads lastEventId from localStorage (FR-008)
 *  3. Connects WS with ?lastEventId= if available
 *  4. Fetches REST snapshot (hydrates Redux slice)
 *  5. Begins receiving live events
 *
 * Spec 017 FR-007, FR-008, FR-009, FR-011, FR-012, FR-016, T017-24, T017-29.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { taskThreadsActions } from '@logic/reducers/task-threads-reducer'
import { getIssue, listIssues, registerSurfaceWorktree } from '@src/service/services/task-threads/task-thread-client'
import type { LiveEvent, IssueShape } from '@logic/types/task-thread'
import type { RootState } from '@logic/types/types'

// ---------------------------------------------------------------------------
// LocalStorage persistence for lastEventId (FR-008)
// ---------------------------------------------------------------------------

const LAST_EVENT_ID_KEY = (companyId: string) =>
  `taskThreads.lastEventId.${companyId}`

function readLastEventId(companyId: string): string | null {
  try {
    return localStorage.getItem(LAST_EVENT_ID_KEY(companyId))
  } catch {
    return null
  }
}

function writeLastEventId(companyId: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(LAST_EVENT_ID_KEY(companyId))
    } else {
      localStorage.setItem(LAST_EVENT_ID_KEY(companyId), value)
    }
  } catch {
    // Ignore storage quota errors
  }
}

// ---------------------------------------------------------------------------
// equanaut-master WebSocket URL builder
// ---------------------------------------------------------------------------

const EQUANAUT_WS_URL: string =
  (typeof process !== 'undefined' && (process.env as any).REACT_APP_EQUANAUT_WS_URL) ||
  (typeof window !== 'undefined'
    ? window.location.origin.replace(/^http/, 'ws')
    : '')

function wsUrl(companyId: string, lastEventId: string | null): string {
  const base = `${EQUANAUT_WS_URL}/api/companies/${companyId}/events/ws`
  return lastEventId ? `${base}?lastEventId=${encodeURIComponent(lastEventId)}` : base
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseTaskThreadSubscriptionOptions {
  companyId: string | null
  /** Better-Auth session identity for worktree registration (FR-016) */
  surfaceIdentity?: string
  enabled?: boolean
}

export function useTaskThreadSubscription({
  companyId,
  surfaceIdentity = 'equa-web-session',
  enabled = true,
}: UseTaskThreadSubscriptionOptions): void {
  const dispatch = useDispatch<Dispatch<AnyAction>>()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)
  const backoffRef = useRef(0)
  const subscriptionGenerationRef = useRef(0)
  const taskItems = useSelector(
    (state: RootState) =>
      ((state as any).taskThreads?.items ?? {}) as Record<string, unknown>
  )
  const subscribedCompanyId = useSelector(
    (state: RootState) => ((state as any).taskThreads?.companyId ?? null) as string | null
  )
  const appliedLastEventId = useSelector(
    (state: RootState) => ((state as any).taskThreads?.lastEventId ?? null) as string | null
  )
  const taskItemsRef = useRef(taskItems)

  useEffect(() => {
    taskItemsRef.current = taskItems
  }, [taskItems])

  useEffect(() => {
    if (subscribedCompanyId) {
      writeLastEventId(subscribedCompanyId, appliedLastEventId)
    }
  }, [appliedLastEventId, subscribedCompanyId])

  // ---------------------------------------------------------------------------
  // Per-event handler — dispatches to Redux slice
  // ---------------------------------------------------------------------------
  const handleMessage = useCallback(
    (raw: string, currentCompanyId: string, generation: number) => {
      if (!mountedRef.current || generation !== subscriptionGenerationRef.current) return
      let event: LiveEvent
      try {
        event = JSON.parse(raw)
      } catch {
        console.warn('[useTaskThreadSubscription] failed to parse WS message')
        return
      }
      // Dispatch to reducer (INV-001..INV-011 enforced in reducer)
      dispatch(taskThreadsActions.event(event))
      if (
        event.companyId === currentCompanyId &&
        event.type === 'task.status_changed' &&
        !taskItemsRef.current[event.task.id]
      ) {
        void getIssue(currentCompanyId, event.task.id)
          .then(issue => {
            if (mountedRef.current && generation === subscriptionGenerationRef.current) {
              dispatch(taskThreadsActions.hydrateOne(issue))
            }
          })
          .catch(err => {
            console.error('[useTaskThreadSubscription] hydrateOne failed:', err)
          })
      }
    },
    [dispatch]
  )

  // ---------------------------------------------------------------------------
  // REST hydrate
  // ---------------------------------------------------------------------------
  const hydrate = useCallback(
    async (currentCompanyId: string) => {
      dispatch(taskThreadsActions.setStatus('loading'))
      try {
        let allIssues: IssueShape[] = []
        let cursor: string | null = null
        do {
          const page = await listIssues(currentCompanyId, cursor, 'all', 100)
          allIssues = allIssues.concat(page.items)
          cursor = page.nextCursor
        } while (cursor)
        dispatch(taskThreadsActions.hydrate(allIssues))
      } catch (err) {
        console.error('[useTaskThreadSubscription] hydrate failed:', err)
        dispatch(taskThreadsActions.setStatus('offline', String(err)))
      }
    },
    [dispatch]
  )

  // ---------------------------------------------------------------------------
  // WS connect
  // ---------------------------------------------------------------------------
  const connect = useCallback(
    (currentCompanyId: string, worktreeLastEventId: string | null, generation: number) => {
      if (!mountedRef.current || generation !== subscriptionGenerationRef.current) return
      const storedLastEventId = readLastEventId(currentCompanyId)
      // Use worktree-provided lastEventId as the initial cursor on first connect
      const lastEventId = storedLastEventId ?? worktreeLastEventId
      const url = wsUrl(currentCompanyId, lastEventId)

      dispatch(taskThreadsActions.setStatus('loading'))
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.addEventListener('open', () => {
        if (!mountedRef.current || generation !== subscriptionGenerationRef.current) {
          ws.close()
          return
        }
        backoffRef.current = 0
        dispatch(taskThreadsActions.setStatus('live'))
      })

      ws.addEventListener('message', (ev: MessageEvent) => {
        handleMessage(ev.data as string, currentCompanyId, generation)
      })

      ws.addEventListener('close', (ev: CloseEvent) => {
        if (!mountedRef.current || generation !== subscriptionGenerationRef.current) return
        // FR-011 — close code 4040 = full_refetch_required
        if (ev.code === 4040) {
          writeLastEventId(currentCompanyId, null)
          dispatch(taskThreadsActions.fullRefetch())
          void hydrate(currentCompanyId).then(() => {
            if (mountedRef.current && generation === subscriptionGenerationRef.current) {
              connect(currentCompanyId, null, generation)
            }
          })
          return
        }
        // 1008 = Policy Violation (auth expiry) — page reload is the simplest
        // recovery in a Better-Auth cookie session; the cookie is refreshed by
        // the auth middleware on the next navigation (FR-016).
        if (ev.code === 1008) {
          dispatch(taskThreadsActions.setStatus('offline', 'auth-expired'))
          if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
            window.location.reload()
            return
          }
          scheduleReconnect(currentCompanyId, null, generation)
          return
        }
        dispatch(taskThreadsActions.setStatus('reconnecting'))
        scheduleReconnect(currentCompanyId, null, generation)
      })

      ws.addEventListener('error', () => {
        if (!mountedRef.current || generation !== subscriptionGenerationRef.current) return
        dispatch(taskThreadsActions.setStatus('reconnecting'))
      })
    },
    [dispatch, handleMessage, hydrate]
  )

  // ---------------------------------------------------------------------------
  // Reconnect with exponential backoff
  // ---------------------------------------------------------------------------
  const scheduleReconnect = useCallback(
    (currentCompanyId: string, lastEventId: string | null, generation: number) => {
      if (generation !== subscriptionGenerationRef.current) return
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      const delay = Math.min(1000 * Math.pow(2, backoffRef.current), 30000)
      backoffRef.current = Math.min(backoffRef.current + 1, 5)
      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current && generation === subscriptionGenerationRef.current) {
          connect(currentCompanyId, lastEventId, generation)
        }
      }, delay)
    },
    [connect]
  )

  // ---------------------------------------------------------------------------
  // Mount / unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true
    const generation = subscriptionGenerationRef.current + 1
    subscriptionGenerationRef.current = generation
    if (!companyId || !enabled) return

    dispatch(taskThreadsActions.setCompany(companyId))

    let cancelled = false

    const init = async () => {
      // T017-29: Call POST /worktrees on first subscription (FR-012)
      const { worktreeId, lastEventId: worktreeLastEventId } =
        await registerSurfaceWorktree(companyId, 'equa_web_sidebar', surfaceIdentity)
      if (cancelled || generation !== subscriptionGenerationRef.current) return
      if (worktreeId) {
        dispatch(taskThreadsActions.setWorktree(worktreeId))
      }
      // Hydrate REST snapshot, then connect WS
      await hydrate(companyId)
      if (cancelled || generation !== subscriptionGenerationRef.current) return
      connect(companyId, worktreeLastEventId, generation)
    }

    void init()

    return () => {
      cancelled = true
      if (subscriptionGenerationRef.current === generation) {
        subscriptionGenerationRef.current += 1
      }
      mountedRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (wsRef.current) {
        wsRef.current.close(1000, 'component unmounted')
        wsRef.current = null
      }
    }
  }, [companyId, connect, dispatch, enabled, hydrate, surfaceIdentity])
}
